import { useState, type ReactNode } from 'react'
import { Modal } from '@/shared/components/Modal'
import type { ModalName } from '@/lib/modalTelemetry'

interface Props {
  /** Which confirmation this is, for telemetry - the generic shell cannot name itself. */
  track: ModalName
  title: string
  message: ReactNode
  confirmLabel?: string
  /** If set, the user must type this exact phrase before the destructive action is enabled. */
  confirmPhrase?: string
  /** Hint shown above the confirm input. Defaults to a generic instruction referencing confirmPhrase. */
  confirmHint?: ReactNode
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function DangerConfirmModal({
  track,
  title,
  message,
  confirmLabel = 'Delete',
  confirmPhrase,
  confirmHint,
  busy,
  onConfirm,
  onCancel,
}: Props) {
  const [typed, setTyped] = useState('')
  const phraseRequired = !!confirmPhrase
  const confirmEnabled = !phraseRequired || typed === confirmPhrase

  return (
    <Modal track={track} title={title} onClose={onCancel} maxWidth={440}>
      <div style={{ fontSize: '0.85rem', color: 'var(--text2)', marginBottom: phraseRequired ? 16 : 24, lineHeight: 1.6 }}>
        {message}
      </div>

      {phraseRequired && (
        <>
          <p style={{ fontSize: '0.85rem', color: 'var(--text)', marginBottom: 8 }}>
            {confirmHint ?? (
              <>Type <code style={{ color: 'var(--accent)' }}>{confirmPhrase}</code> to confirm:</>
            )}
          </p>
          <input
            type="text"
            className="input"
            placeholder={confirmPhrase}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            style={{ marginBottom: 24 }}
          />
        </>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-danger btn-sm"
          onClick={onConfirm}
          disabled={busy || !confirmEnabled}
        >
          {busy ? <span className="spinner" /> : confirmLabel}
        </button>
      </div>
    </Modal>
  )
}
